import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Space, Tooltip } from "antd";
import { Button, Col, Dropdown, Form, Modal, Row } from "react-bootstrap";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  formatDate,
  isoDateFormat,
  isValidDecimalInput,
} from "../../utils/Utils";
import {
  addStripeCalendarDays,
  formatStripeCalendarDate,
  getStripeUtcDayAfterTomorrowMinDate,
  getStripeUtcFirstOfNextMonthMinDate,
  getStripeUtcTomorrowMinDate,
  isStripeUtcDateOnOrAfter,
  isStripeUtcDateOnOrBefore,
  isStripeUtcFutureDate,
  parseStripeCalendarDateFromApi,
} from "../../utils/dateUtils";
import useCustomTranslation from "../../hooks/useCustomTranslation";
import {
  getPaymentHistory,
  updatePaymentDetails,
  clearUpdateMessage,
  setPagination,
  resetPagination,
  pauseClientSubscription,
  clearPauseMessage,
  endClientSubscription,
  clearEndMessage,
  retryPaymentDetails,
  refundPaymentDetails,
  clearRetryMessage,
  clearRefundMessage,
  extendClientSubscription,
  clearExtendMessage
} from "../../redux/Slices/clientPaymentTabSlice";
import {
  postSendPaymentMethodUpdateEmailApi
} from "../../api/clientPaymentTabApi";
import FormLabel from "../../components/Common/FormLabel";
import Loader from "../../components/Common/Loader";
import FormControl from "../../components/Common/FormControl";
import { Table, Typography } from "antd";
import ToastNotification from "../../components/Common/ToastNotification";
import appInsights from "../../AppInsights";
import { toast } from "react-toastify";
import { CONSTANTS } from "../../utils/Constant/Constant";
import { fetchMembershipOverview } from "../../redux/Slices/membershipOverviewSlice";

const { Text } = Typography;

const isOneTimePayment = (payment) => {
  if (payment?.isOneTimePayment === true) return true;
  const paymentType = (payment?.paymentType || "")
    .toLowerCase()
    .replace(/-/g, " ");
  return paymentType.includes("one time") || paymentType.includes("onetime");
};

const findRecurringNeighborPayment = (sortedPayments, currentIndex, direction) => {
  for (
    let index = currentIndex + direction;
    direction < 0 ? index >= 0 : index < sortedPayments.length;
    index += direction
  ) {
    if (!isOneTimePayment(sortedPayments[index])) {
      return sortedPayments[index];
    }
  }
  return null;
};

const getLastPaymentDueDate = (payments, subscriptionEndDate) => {
  const recurringPayments = payments
    .filter((payment) => payment.dueDate && !isOneTimePayment(payment))
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  if (recurringPayments.length > 0) {
    return parseStripeCalendarDateFromApi(
      recurringPayments[recurringPayments.length - 1].dueDate
    );
  }

  return subscriptionEndDate
    ? parseStripeCalendarDateFromApi(subscriptionEndDate)
    : null;
};

const getTableEditDueDateBounds = (
  selectedPayment,
  payments,
  subscriptionEndDate
) => {
  let minDate = getStripeUtcTomorrowMinDate();
  let maxDate = null;

  if (!selectedPayment?.paymentId || !payments?.length) {
    return { minDate, maxDate };
  }

  if (isOneTimePayment(selectedPayment)) {
    return {
      minDate: getStripeUtcTomorrowMinDate(),
      maxDate: getLastPaymentDueDate(payments, subscriptionEndDate),
    };
  }

  const sortedPayments = [...payments]
    .filter((payment) => payment.dueDate)
    .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

  const currentIndex = sortedPayments.findIndex(
    (payment) => payment.paymentId === selectedPayment.paymentId
  );

  if (currentIndex === -1) {
    return { minDate, maxDate };
  }

  const previousPayment = findRecurringNeighborPayment(
    sortedPayments,
    currentIndex,
    -1
  );
  const nextPayment = findRecurringNeighborPayment(
    sortedPayments,
    currentIndex,
    1
  );

  if (previousPayment) {
    const dayAfterPrevious = addStripeCalendarDays(previousPayment.dueDate, 1);
    if (
      dayAfterPrevious &&
      isStripeUtcDateOnOrAfter(dayAfterPrevious, minDate)
    ) {
      minDate = dayAfterPrevious;
    }
  }

  if (nextPayment) {
    maxDate = addStripeCalendarDays(nextPayment.dueDate, -1);
  } else if (previousPayment) {
    const previousDate = parseStripeCalendarDateFromApi(previousPayment.dueDate);
    const expectedDueDate = new Date(
      previousDate.getFullYear(),
      previousDate.getMonth() + 1,
      previousDate.getDate()
    );
    maxDate = addStripeCalendarDays(expectedDueDate, 29);
  }

  return { minDate, maxDate };
};

const ClientPaymentDetailsTab = ({ selectedClientId, coachId }) => {
  const { trans } = useCustomTranslation();

  const dispatch = useDispatch();
  const {
    status,
    paymentMonthStatus,
    paymentOverview,
    subscriptionEndDate,
    loading,
  } = useSelector((state) => state.membershipOverview);

  const {
    paymentData,
    totalCount,
    currentPage,
    pageSize,
    paymentDataLoading,
    updateSuccessMessage,
    updateFailedMessage,
    pauseSuccessMessage,
    pauseFailedMessage,
    endSuccessMessage,
    endFailedMessage,
    extendSuccessMessage,
    extendFailedMessage,
    retrySuccessMessage,
    retryFailedMessage,
    retryLoading,
    refundSuccessMessage,
    refundFailedMessage,
    refundLoading,
  } = useSelector((state) => state.payment);

  const [filteredData, setFilteredData] = useState([]);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectClientStatus, setSelectedClientStatus] = useState(
    status || "Active"
  );
  const [isPauseSubscription, setIsPauseSubscription] = useState(false);
  const [isEndSubscription, setIsEndSubscription] = useState(false);
  const [isExtendSubscription, setIsExtendSubscription] = useState(false);
  const [isRetry, setIsRetry] = useState(false);
  const [isRefund, setIsRefund] = useState(false);
  const [refundPaymentIds, setRefundPaymentIds] = useState("");
  const [isSubscriptionConfirmation, setIsSubscriptionConfirmation] =
    useState(false);
  const [
    subscriptionConfirmationMessage,
    setIsSubscriptionConfirmationMessage,
  ] = useState("");
  const [selectedOption, setSelectedOption] = useState(
    "endSubscriptionAfterCurrentPaymentPeriod"
  );

  const [selectedPayment, setSelectedPayment] = useState(null);
  const [currencyValue, setCurrencyValue] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [updateFromDate, setUpdateFromDate] = useState(null);
  const [resumeSubscriptionDate, setResumeSubscriptionDate] = useState(null);
  const [isEditFromTable, setIsEditFromTable] = useState(false);
  const [toastConfig, setToastConfig] = useState(null);
  const [errors, setErrors] = useState({
    currencyValue: "",
    dueDate: "",
    resumeSubscriptionDate: "",
  });
  const [pauseSubscriptionPayload, setPauseSubscriptionPayload] = useState({});
  const [endSubscriptionPayload, setEndSubscriptionPayload] = useState({});
  const [extendSubscriptionPayload, setExtendSubscriptionPayload] = useState({});
  const [nextDueDate, setNextDueDate] = useState(null);
  const [retryingPaymentId, setRetryingPaymentId] = useState(null);
  const [refundPaymentId, setRefundPaymentId] = useState(null);
  const [extendMonths, setExtendMonths] = useState("");
  const [isChangePaymentMethod, setIsChangePaymentMethod] = useState(false);
  const [paymentMethodLoading, setPaymentMethodLoading] = useState(false);
  const [isEditMonthlyPriceLoading, setIsEditMonthlyPriceLoading] = useState(false);
  const clientId = selectedClientId;

  const tableEditDueDateBounds = useMemo(
    () =>
      getTableEditDueDateBounds(
        selectedPayment,
        filteredData,
        subscriptionEndDate
      ),
    [selectedPayment, filteredData, subscriptionEndDate]
  );

  const billingCycles = [
    { value: "monthApart", label: trans("LeadPage.monthApart") },
    { value: "startOfTheMonth", label: trans("LeadPage.startOfTheMonth") },
    {
      value: "lastWeekDayOfMonth",
      label: trans("LeadPage.lastWeekDayOfMonth"),
    },
    { value: "custom", label: trans("LeadPage.custom") },
  ];

  // AppInsights – Page View
  useEffect(() => {
    appInsights.trackPageView({
      name: "ClientPaymentDetailsTab",
      properties: {
        clientId,
        coachId,
      },
    });
  }, [clientId, coachId]);

  useEffect(() => {
    if (clientId) {
      dispatch(
        getPaymentHistory({
          clientId,
          pageNumber: currentPage,
          pageSize,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  useEffect(() => {
    return () => {
      dispatch(resetPagination());
    };
  }, [dispatch]);

  // Sync filteredData with paymentData
  useEffect(() => {
    setFilteredData(paymentData);
  }, [paymentData]);

  useEffect(() => {
    if (filteredData && filteredData.length > 0) {
      const today = new Date();
      const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 1);

      const sortedPayments = filteredData
        .filter((payment) => payment.payStatus === "Pending")
        .sort((a, b) => new Date(a.dueDate) - new Date(b.dueDate));

      const nextDue = sortedPayments.find(
        (payment) => new Date(payment.dueDate) >= today
      );
      const nextMonthDue = sortedPayments.find(
        (payment) => new Date(payment.dueDate) >= nextMonth
      );

      if (nextDue) {
        setNextDueDate(nextDue.dueDate);
      } else if (nextMonthDue) {
        setNextDueDate(nextMonthDue.dueDate);
      }
    }
  }, [filteredData]);

  // Payment bar color logic
  const getPaymentBarColor = (status) => {
    switch (status) {
      case "Received":
      case "Success":
        return "#28A745";
      case "Failed":
      case "Cancelled":
        return "#DC3545";
      case "Pending":
      case "Processing":
      case "Next":
        return "#A0A0A0";
      default:
        return "#6C757D";
    }
  };

  // Handle currency value input changes
  const onCurrencyValueChange = (e) => {
    const { value } = e.target;
    if (value === "0") {
      setErrors({
        ...errors,
        currencyValue: trans("ClientPaymentTab.validAmount"),
      });
    } else if (isValidDecimalInput(value)) {
      setCurrencyValue(value);
      setErrors({ ...errors, currencyValue: "" });
    }

    if (!value) {
      setErrors({
        ...errors,
        currencyValue: trans("ClientPaymentTab.validAmount"),
      });
    }
  };

  // Validate form inputs
  const validateForm = (action = {}) => {
    const refundActive = action.isRefund ?? isRefund;
    const retryActive = action.isRetry ?? isRetry;

    if (isEndSubscription) {
      return true;
    }
    let isValid = true;
    const newErrors = {
      currencyValue: "",
      dueDate: "",
      resumeSubscriptionDate: "",
      month: ""
    };

    if (
      (!currencyValue || currencyValue === "0") &&
      !isPauseSubscription &&
      !isEndSubscription &&
      !isExtendSubscription &&
      !refundActive &&
      !retryActive
    ) {
      newErrors.currencyValue = trans("ClientPaymentTab.validAmount");
      isValid = false;
    }

    if (isEditFromTable && !dueDate) {
      newErrors.dueDate = trans("ClientPaymentTab.validDueDate");
      isValid = false;
    } else if (isEditFromTable && dueDate) {
      const { minDate, maxDate } = tableEditDueDateBounds;
      if (
        !isStripeUtcDateOnOrAfter(dueDate, minDate) ||
        (maxDate && !isStripeUtcDateOnOrBefore(dueDate, maxDate))
      ) {
        newErrors.dueDate = trans("ClientPaymentTab.validDueDate");
        isValid = false;
      }
    }
    if (!isEditFromTable && !updateFromDate && !isPauseSubscription && !isExtendSubscription && !refundActive && !retryActive) {
      newErrors.updateFromDate = trans("ClientPaymentTab.validFromDate");
      isValid = false;
    } else if (
      !isEditFromTable &&
      updateFromDate &&
      !isStripeUtcDateOnOrAfter(
        updateFromDate,
        getStripeUtcFirstOfNextMonthMinDate()
      )
    ) {
      newErrors.updateFromDate = trans("ClientPaymentTab.validFromDate");
      isValid = false;
    }

    if (isPauseSubscription && !resumeSubscriptionDate) {
      newErrors.resumeSubscriptionDate = trans(
        "ClientPaymentTab.validResumeDate"
      );
      isValid = false;
    } else if (
      isPauseSubscription &&
      resumeSubscriptionDate &&
      !isStripeUtcDateOnOrAfter(
        resumeSubscriptionDate,
        getStripeUtcDayAfterTomorrowMinDate()
      )
    ) {
      newErrors.resumeSubscriptionDate = trans(
        "ClientPaymentTab.validResumeDate"
      );
      isValid = false;
    }
    if (isExtendSubscription && extendMonths === "") {
      newErrors.month = trans(
        "ClientPaymentTab.validExtendMonths"
      );
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  // Handle modal confirmation
  const handleConfirm = async (action = {}) => {
    const refundActive = action.isRefund ?? isRefund;
    const retryActive = action.isRetry ?? isRetry;

    if (!validateForm(action)) {
      // Stop if validation fails
      return;
    }

    if (isPauseSubscription) {
      // Handle Pause Subscription logic
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      setIsSubscriptionConfirmationMessage(
        `${trans(
          "ClientPaymentTab.resumeSubscriptionConfirmationMessage"
        )} ${formatDate(resumeSubscriptionDate)}`
      );
      const payload = {
        clientId: clientId,
        resumeDate: resumeSubscriptionDate
          ? formatStripeCalendarDate(resumeSubscriptionDate)
          : null,
        coachId,
        pageNumber: currentPage,
        pageSize,
      };
      setPauseSubscriptionPayload(payload);
    } else if (isEndSubscription) {
      // Handle End Subscription logic
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      selectedOption === "endSubscriptionToday"
        ? setIsSubscriptionConfirmationMessage(
          trans("ClientPaymentTab.endSubscriptionConfirmationMessage")
        )
        : setIsSubscriptionConfirmationMessage(
          `Are you sure you want to end this subscription after the current payment period? The subscription will remain active until ${nextDueDate ? formatDate(nextDueDate) : ""
          }`
        );
      const payload = {
        clientId,
        endImmediately: selectedOption === "endSubscriptionToday",
        endAfterBilling:
          selectedOption === "endSubscriptionAfterCurrentPaymentPeriod",
        coachId,
        pageNumber: currentPage,
        pageSize,
      };
      setEndSubscriptionPayload(payload);
    } else if (isExtendSubscription) {
      // Handle Extend Subscription logic
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      setIsSubscriptionConfirmationMessage(
        `${trans("Are you sure want to extend the subscription")} ${extendMonths} month${extendMonths > 1 ? "s" : ""}`
      );
      const payload = {
        clientId
        , extendMonths,
        pageNumber: currentPage,
        pageSize,
      };
      setExtendSubscriptionPayload(payload);
    } else if (refundActive) {
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      setIsSubscriptionConfirmationMessage(
        `${trans("Are you sure you want to refund this payment? The refunded amount will be returned to the client's payment method and this action cannot be undone.")} `
      );
    } else if (retryActive) {
      setIsDialogOpen(false);
      setIsSubscriptionConfirmation(true);
      setIsSubscriptionConfirmationMessage(
        `${trans("Are you sure you want to retry this payment? Stripe will attempt to charge the client's saved payment method again.")} `
      );
    }
    else if (isEditFromTable) {
      setIsDialogOpen(false);
      if (selectedPayment) {
        const paymentDetails = {
          paymentId: selectedPayment?.paymentId,
          isMonthlyPayment: true,
          amount: currencyValue,
          dueDate: dueDate ? isoDateFormat(dueDate) : null,
          updateFromDate: null,
          updatedBy: coachId
        };

        setSelectedPayment(null);
        setCurrencyValue("");
        setDueDate(null);
        setIsEditFromTable(false);
        setIsEditMonthlyPriceLoading(true);
        await dispatch(
          updatePaymentDetails({
            clientId,
            paymentDetails,
            pageNumber: currentPage,
            pageSize,
          })
        );
        setIsEditMonthlyPriceLoading(false);
      }
    } else {
      setIsDialogOpen(false);
      const paymentDetails = {
        paymentId: null,
        isMonthlyPayment: false,
        amount: currencyValue,
        dueDate: null,
        updateFromDate: isoDateFormat(updateFromDate),
        updatedBy: coachId
      };
      setUpdateFromDate(null);
      setIsEditFromTable(false);
      setIsEditMonthlyPriceLoading(true);
      // const clientId = "45AF361A-721B-4730-847E-6B838585C3F6";
      await dispatch(
        updatePaymentDetails({
          clientId,
          paymentDetails,
          pageNumber: currentPage,
          pageSize,
        })
      );
      // Close the modal after updating the data

      setIsEditMonthlyPriceLoading(false);
    }
    //  window.location.reload();

  };

  const handleSubscriptionConfirm = async () => {
    appInsights.trackEvent({
      name: isPauseSubscription
        ? "ConfirmPauseSubscription"
        : isEndSubscription
          ? "ConfirmEndSubscription"
          : isExtendSubscription
            ? "ConfirmExtendSubscription"
            : isChangePaymentMethod
              ? "ConfirmChangePaymentMethod"
              : "ConfirmSubscriptionAction",
      properties: {
        clientId,
        coachId,
      },
    });
    setPaymentMethodLoading(true);
    setIsSubscriptionConfirmation(false);
    if (isChangePaymentMethod) {
      // 👇 now you can await this API call
      const response = await postSendPaymentMethodUpdateEmailApi(clientId, coachId);
      if (!toast.isActive("success-toast")) {
        toast.success(
          <div>
            <p>{response}</p>
          </div>,
          {
            position: "top-center",
            toastId: "success-toast",
            autoClose: CONSTANTS.TOAST_DURATION_LONG,
            hideProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          }
        );

      }
    } else if (isRefund) {
      const result = await handleRefundPayment(refundPaymentIds);
      if (refundPaymentDetails.fulfilled.match(result)) {
        setRefundPaymentIds("");
        setIsRefund(false);
      }
    } else if (isRetry) {
      const result = await handleRetryPayment(refundPaymentIds);
      if (retryPaymentDetails.fulfilled.match(result)) {
        setRefundPaymentIds("");
        setIsRetry(false);
        await dispatch(fetchMembershipOverview(clientId));
      }
    } else if (isPauseSubscription) {
      await dispatch(pauseClientSubscription(pauseSubscriptionPayload));
      await dispatch(fetchMembershipOverview(clientId));
      setResumeSubscriptionDate(null);
      setPauseSubscriptionPayload({});
      setIsPauseSubscription(false);
    } else if (isEndSubscription) {
      await dispatch(endClientSubscription(endSubscriptionPayload));
      await dispatch(fetchMembershipOverview(clientId));
      setEndSubscriptionPayload({});
      setIsEndSubscription(false);
    }
    else if (isExtendSubscription) {
      await dispatch(extendClientSubscription(extendSubscriptionPayload));
      setTimeout(async () => {
        await dispatch(getPaymentHistory({ clientId, pageNumber: currentPage, pageSize }));
        await dispatch(fetchMembershipOverview(clientId));

      }, 10000);
      setExtendSubscriptionPayload({});
      setIsExtendSubscription(false);
    }
    setIsSubscriptionConfirmation(false);
    setIsChangePaymentMethod(false);
    setPaymentMethodLoading(false);
  };
  // Open the dialog for editing a payment record from the table row
  const openDialogFromTable = (payment) => {
    appInsights.trackEvent({
      name: "EditPaymentFromTable",
      properties: {
        clientId,
        coachId,
        paymentId: payment?.paymentId,
      },
    });

    setSelectedPayment(payment);
    setCurrencyValue(payment.amount);
    setDueDate(parseStripeCalendarDateFromApi(payment.dueDate));
    setIsEditFromTable(true);
    setIsDialogOpen(true);
    setIsPauseSubscription(false);
    setIsEndSubscription(false);
    setIsExtendSubscription(false);
  };

  // Open the dialog for editing a recurring payment (not from table)
  const openDialogFromPencil = () => {
    appInsights.trackEvent({
      name: "EditRecurringPayment",
      properties: {
        clientId,
        coachId,
      },
    });

    setSelectedPayment(null);
    setUpdateFromDate(null);
    setCurrencyValue(
      paymentOverview?.monthlyAmount ? paymentOverview?.monthlyAmount : 0
    );
    setDueDate(null);
    setIsEditFromTable(false);
    setIsPauseSubscription(false);
    setIsEndSubscription(false);
    setIsExtendSubscription(false);
    setIsDialogOpen(true);
  };

  // Reset form fields used by edit / pause / end / extend dialogs
  const resetDialogFormState = () => {
    setIsEditFromTable(false);
    setSelectedPayment(null);
    setCurrencyValue("");
    setDueDate(null);
    setUpdateFromDate(null);
    setResumeSubscriptionDate(null);
    setExtendMonths("");
    setErrors({
      currencyValue: "",
      dueDate: "",
      resumeSubscriptionDate: "",
      month: "",
    });
  };

  // Reset action flags used by subscription / retry / refund flows
  const resetSubscriptionActionState = () => {
    setIsPauseSubscription(false);
    setIsEndSubscription(false);
    setIsExtendSubscription(false);
    setIsRetry(false);
    setIsRefund(false);
    setIsChangePaymentMethod(false);
    setRefundPaymentIds("");
    setPauseSubscriptionPayload({});
    setEndSubscriptionPayload({});
    setExtendSubscriptionPayload({});
    setSelectedOption("endSubscriptionAfterCurrentPaymentPeriod");
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    resetDialogFormState();
    resetSubscriptionActionState();
  };

  const closeSubscriptionConfirmation = () => {
    setIsSubscriptionConfirmation(false);
    resetSubscriptionActionState();
  };

  const handleRetryPayment = async (paymentId) => {
    appInsights.trackEvent({
      name: "RetryPayment",
      properties: {
        clientId,
        coachId,
        paymentId: paymentId,
      },
    });

    setRetryingPaymentId(paymentId);
    return dispatch(
      retryPaymentDetails({
        clientId,
        paymentId: paymentId,
        coachId,
        pageNumber: currentPage,
        pageSize,
      })
    );
  };

  const handleRefundPayment = async (paymentId) => {
    appInsights.trackEvent({
      name: "RefundPayment",
      properties: {
        clientId,
        coachId,
        paymentId: paymentId,
      },
    });

    setRefundPaymentId(paymentId);
    return dispatch(
      refundPaymentDetails({
        clientId,
        paymentId: paymentId,
        coachId,
        pageNumber: currentPage,
        pageSize,
      })
    );
  };

  // Define columns for the table
  const columns = [
    {
      title: trans("Common.amount"),
      dataIndex: "amount",
      key: "amount",
      render: (text, record) => `${record?.currency} ${text}`,
    },
    {
      title: trans("Common.type"),
      dataIndex: "paymentType",
      key: "paymentType",
      render: (paymentType) => {
        let backgroundClass = "";
        let text = paymentType;

        switch (paymentType) {
          case "paid":
            backgroundClass = "bg-teal-100";
            text = "Paid";
            break;
          case "Monthly":
            backgroundClass = "bg-inactive";
            text = "Monthly Payment";
            break;
          case "NotSetup":
            backgroundClass = "bg-invalid";
            text = "Not Setup";
            break;
          default:
            backgroundClass = "bg-invalid";
            text = paymentType;
            break;
        }

        return (
          <span
            className={`status-badge ${backgroundClass} d-flex justify-content-center`}
          >
            {text}
          </span>
        );
      },
    },
    {
      title: trans("ClientPaymentTab.dueDate"),
      dataIndex: "dueDate",
      key: "dueDate",
      render: (dueDate) => <span>{dueDate ? formatDate(dueDate) : "-"}</span>,
    },
    {
      title: trans("ClientPaymentTab.paidDate"),
      dataIndex: "paidDate",
      key: "paidDate",
      render: (paidDate) => (
        <span>{paidDate ? formatDate(paidDate) : "-"}</span>
      ),
    },
    {
      title: trans("Common.status"),
      dataIndex: "payStatus",
      key: "payStatus",
      render: (text) => `${text}`,
    },
    {
      title: trans("Common.action"),
      key: "action",
      render: (text, record) => {
        const isPaused = record.payStatus === "Paused";
        const isFutureDate = isStripeUtcFutureDate(record.dueDate) && !isPaused;

        return (
          <Space size="small">
            {isFutureDate && (
              <Tooltip title={"Edit"}>
                <Button
                  type="button"
                  disabled={
                    selectClientStatus === "Inactive" ||
                    selectClientStatus === "InActive"
                  }
                  onClick={() => openDialogFromTable(record)}
                  className="action-buttons pl-0"
                >
                  <i className="bi bi-pencil"></i>
                </Button>
              </Tooltip>
            )}

            {/*Retry button — shown only when payment failed AND retry allowed */}
            {record?.payStatus === "Failed" && !record?.isPaymentRetry && (
              <Tooltip title={"Retry Payment"}>
                <Button
                  type="button"
                  disabled={
                    selectClientStatus === "Inactive" ||
                    selectClientStatus === "InActive" ||
                    (retryLoading && retryingPaymentId === record?.paymentId)
                  }
                  onClick={() => {
                    setIsRetry(true);
                    setRefundPaymentIds(record.paymentId);
                    handleConfirm({ isRetry: true });
                  }
                  }
                  className="action-buttons pl-0"
                >
                  {retryLoading && retryingPaymentId === record?.paymentId ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Retrying...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-clockwise"></i><br />
                      Retry
                    </>
                  )}
                </Button>
              </Tooltip>
            )}
            {record?.payStatus === "Received" && !record?.isPaymentRefund && (
              <Tooltip title={"Refund Payment"}>
                <Button
                  type="button"
                  disabled={
                    selectClientStatus === "Inactive" ||
                    selectClientStatus === "InActive" ||
                    (refundLoading && refundPaymentId === record?.paymentId)
                  }
                  onClick={() => {
                    setIsRefund(true);
                    setRefundPaymentIds(record.paymentId);
                    handleConfirm({ isRefund: true });
                  }
                  }
                  className="action-buttons pl-0"
                >
                  {refundLoading && refundPaymentId === record?.paymentId ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      refunding...
                    </>
                  ) : (
                    <>
                      <i className="bi bi-arrow-return-left"></i><br />
                      Refund
                    </>
                  )}
                </Button>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
  ];

  // const totalCount = filteredData.length;

  const handlePageChange = (pagination) => {
    const { current, pageSize } = pagination;

    appInsights.trackEvent({
      name: "PaymentPaginationChange",
      properties: {
        clientId,
        coachId,
        page: current,
        pageSize,
      },
    });

    dispatch(setPagination({ page: current, pageSize }));

    dispatch(
      getPaymentHistory({
        clientId,
        pageNumber: current,
        pageSize,
      })
    );
  };

  // Showing text for pagination
  const getShowingText = () => {
    if (filteredData.length <= 1) return "";
    const start = (currentPage - 1) * pageSize + 1;
    const end = Math.min(currentPage * pageSize, totalCount);
    return `${trans("Common.showing")} ${start} ${trans(
      "Common.to"
    )} ${end} ${trans("Common.of")} ${totalCount} ${trans("Common.results")}`;
  };

  const subscriptionMenuItems = [
    { key: "pause", label: "Pause Subscription" },
    { key: "end", label: "End Subscription" },
    { key: "extend", label: "Extend Subscription" },
  ];

  const handleSubscriptionChange = (itemKey) => {
    if (itemKey === "pause") {
      setIsPauseSubscription(true);
      setIsEndSubscription(false);
      setIsExtendSubscription(false);
    } else if (itemKey === "end") {
      setIsPauseSubscription(false);
      setIsEndSubscription(true);
      setIsExtendSubscription(false);
    }
    else if (itemKey === "extend") {
      setIsPauseSubscription(false);
      setIsEndSubscription(false);
      setIsExtendSubscription(true);
    }
    setIsDialogOpen(true);
  };

  // Toast notification logic
  useEffect(() => {
    if (updateSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: updateSuccessMessage,
        heading: trans("Common.success"),
      });
      dispatch(clearUpdateMessage());
    } else if (updateFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: updateFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearUpdateMessage());
    } else if (pauseSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: pauseSuccessMessage,
        heading: trans("Common.success"),
      });
      setSelectedClientStatus("Paused");
      dispatch(clearPauseMessage());
    } else if (pauseFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: pauseFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearPauseMessage());
    } else if (endSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: endSuccessMessage,
        heading: trans("Common.success"),
      });
      setSelectedClientStatus("Paused");
      dispatch(clearEndMessage());
    } else if (endFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: endFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearEndMessage());
    } else if (extendSuccessMessage) {
      // Trigger success toast
      setToastConfig({
        type: "success",
        message: extendSuccessMessage,
        heading: trans("Common.success"),
      });
      setSelectedClientStatus("Active");
      dispatch(clearExtendMessage());
    } else if (extendFailedMessage) {
      // Trigger success toast
      setToastConfig({
        type: "error",
        message: extendFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearExtendMessage());
    } else if (retrySuccessMessage) {
      setRetryingPaymentId(null);
      setToastConfig({
        key: Date.now(),
        type: "success",
        message: retrySuccessMessage,
        heading: trans("Common.success"),
      });
      dispatch(clearRetryMessage());
    } else if (retryFailedMessage) {
      setRetryingPaymentId(null);
      setToastConfig({
        key: Date.now(),
        type: "error",
        message: retryFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearRetryMessage());
    } else if (refundSuccessMessage) {
      setRefundPaymentId(null);
      setToastConfig({
        key: Date.now(),
        type: "success",
        message: refundSuccessMessage,
        heading: trans("Common.success"),
      });
      dispatch(clearRefundMessage());
    } else if (refundFailedMessage) {
      setRefundPaymentId(null);
      setToastConfig({
        key: Date.now(),
        type: "error",
        message: refundFailedMessage,
        heading: trans("Common.failed"),
      });
      dispatch(clearRefundMessage());
    }
  }, [
    dispatch,
    updateSuccessMessage,
    updateFailedMessage,
    pauseSuccessMessage,
    pauseFailedMessage,
    endSuccessMessage,
    endFailedMessage,
    extendSuccessMessage,
    extendFailedMessage,
    retrySuccessMessage,
    retryFailedMessage,
    refundSuccessMessage,
    refundFailedMessage,
    trans,
  ]);

  return (
    <div className="clientDetailsInfoTabContainer-payment">
      {paymentMethodLoading ? (
        <Loader />
      ) : (
        <Row className="align-items-center mb-3">
          <Col xs={12} md={4}>
            <h6>{trans("Common.payment") + " " + trans("Common.overview")}</h6>
          </Col>

          <Col xs={12} md={8} className="text-md-end">
            {selectClientStatus === "Active" && (
              <Row className="g-2 justify-content-md-end">
                <Col xs={12} md="auto">
                  <Button
                    variant="primary"
                    className="saveButton w-100 d-flex align-items-center pt-1 pb-1 fs-6 rounded-5"
                    onClick={() => {
                      setIsChangePaymentMethod(true);
                      setIsSubscriptionConfirmation(true);  // open modal
                      setIsSubscriptionConfirmationMessage(
                        `${trans(
                          "Do you want to send email that allows client to change their payment method?"
                        )}`
                      );
                    }}
                  >
                    Change Payment Method
                  </Button>
                </Col>

                <Col xs={12} md="auto">
                  <Dropdown className="w-100">
                    <Dropdown.Toggle
                      variant="primary"
                      className="saveButton w-90 d-flex align-items-center pt-1 pb-1 fs-6 rounded-5"
                    >
                      Subscription
                    </Dropdown.Toggle>

                    <Dropdown.Menu>
                      {subscriptionMenuItems.map((item) => (
                        <Tooltip
                          key={`tooltip-${item.key}`}
                          placement="right"
                          title={
                            item.key === "pause"
                              ? trans("ClientPaymentTab.pauseSubscriptionTooltip")
                              : item.key === "extend"
                                ? trans("ClientPaymentTab.extendSubscriptionTooltip")
                                : trans("ClientPaymentTab.endSubscriptionTooltip")
                          }
                        >
                          <Dropdown.Item
                            key={item.key}
                            onClick={() => handleSubscriptionChange(item.key)}
                          >
                            {item.label}
                          </Dropdown.Item>
                        </Tooltip>
                      ))}
                    </Dropdown.Menu>
                  </Dropdown>
                </Col>
              </Row>
            )}
          </Col>
        </Row>)}


      {loading || isEditMonthlyPriceLoading || paymentMethodLoading ? (
        <Loader />
      ) : (
        <>
          <Row>
            <Col xs={4} sm={4} md={4} lg={4}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {trans("ClientPaymentTab.monthlyPrice")}
              </p>
              <p
                className="ms-1 fs-6 font-normal"
                disabled={
                  selectClientStatus === "Inactive" ||
                  selectClientStatus === "InActive"
                }
              >
                {(paymentOverview?.currency || "N/A") +
                  " " +
                  (paymentOverview?.monthlyAmount || "N/A")}{" "}
                <i
                  className={`bi bi-pencil-fill ${selectClientStatus === "Inactive" ||
                    selectClientStatus === "InActive"
                    ? "text-muted pointer-events-none"
                    : "cursor-pointer"
                    }`}
                  onClick={() => {
                    if (
                      selectClientStatus !== "Inactive" ||
                      selectClientStatus !== "InActive"
                    ) {
                      openDialogFromPencil();
                    }
                  }}
                ></i>
              </p>
            </Col>
            <Col xs={4} sm={4} md={4} lg={4}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {trans("Common.subscription") + " " + trans("Common.duration")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {paymentOverview?.subscriptionDuration}
              </p>
            </Col>
            <Col xs={4} sm={4} md={4} lg={4}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {trans("Common.billingCycle")}
              </p>
              <p className="ms-1 fs-6 font-normal">
                {billingCycles?.find(
                  (cycle) => cycle.value === paymentOverview?.billingCycle
                )?.label || "N/A"}
              </p>
            </Col>
          </Row>
          <Row>
            <Col xs={3} sm={3} md={3} lg={3}>
              <p className="ms-1 fs-6 font-normal mb-2 text-color-gray-40">
                {`${trans("Common.payments")}`}
              </p>
              <div className="payment-bars">
                {paymentMonthStatus?.map((payment, index) => (
                  <Tooltip
                    key={index}
                    title={
                      <>
                        {payment.date + "  " + payment.month}
                        <br />
                        Payment {payment.paymentStatus}
                        <br />
                        Amount {payment.amount}
                      </>
                    }
                  >
                    <div
                      className="payment-bar"
                      style={{
                        backgroundColor: getPaymentBarColor(
                          payment.paymentStatus
                        ),
                      }}
                    />
                  </Tooltip>
                ))}
              </div>
            </Col>
          </Row>
        </>
      )}
      <Row className="align-items-center mt-3">
        <h6>{trans("Common.payment") + " " + trans("Common.history")}</h6>
      </Row>
      {paymentMethodLoading ? (
        <Loader />
      ) : (
        <Table
          columns={columns}
          dataSource={filteredData}
          loading={paymentDataLoading}
          pagination={{
            className: "custom-pagination-payment",
            current: currentPage,
            pageSize: pageSize,
            total: totalCount,
            showSizeChanger: true,
            pageSizeOptions: ["10", "25", "50", "100"],
            position: ["bottomRight"],
            showTotal: () => <Text>{getShowingText()}</Text>,
          }}
          onChange={handlePageChange}
          rowKey={(record) => record.id}
          locale={{
            emptyText: !paymentDataLoading && "No payment details data available",
          }}
          components={{
            table: (props) => (
              <table
                {...props}
                style={{
                  ...props.style,
                  borderCollapse: "separate",
                  borderSpacing: "0 10px",
                }}
              />
            ),
            header: {
              cell: (props) => {
                return (
                  <th
                    {...props}
                    style={{
                      ...props.style,
                      background: "transparent",
                      borderColor: "#FCFBFFCC",
                      borderStyle: "solid",
                      borderWidth: "8px 0px",
                      fontFamily: "Proxima-Nova-Regular",
                      padding: "12px 16px",
                      // Border radius for header cells
                      borderRadius:
                        props.children === columns[0].title
                          ? "12px 0 0 12px"
                          : props.children === columns[columns.length - 1].title
                            ? "0 12px 12px 0"
                            : "0",
                    }}
                  />
                );
              },
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                  }}
                />
              ),
            },
            body: {
              cell: (props) => (
                <td
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    borderColor: "transparent",
                    borderStyle: "solid",
                    borderWidth: "8px 0px",
                    padding: "12px 16px",
                    fontFamily: "Proxima-Nova-Regular",
                    // Border radius for body cells
                    borderRadius:
                      props.index % columns.length === 0
                        ? "12px 0 0 12px"
                        : props.index % columns.length === columns.length - 1
                          ? "0 12px 12px 0"
                          : "0",
                  }}
                />
              ),
              row: (props) => (
                <tr
                  {...props}
                  style={{
                    ...props.style,
                    background: "transparent",
                    fontFamily: "Proxima-Nova-Regular",
                    margin: "10px 0",
                  }}
                />
              ),
            },
          }}
        />
      )}
      {isDialogOpen && !isPauseSubscription && !isEndSubscription && !isExtendSubscription && (
        <Modal
          show={isDialogOpen}
          onHide={closeDialog}
          centered
          backdrop="static"
          keyboard={false}
        >
          <Modal.Header closeButton>
            <Modal.Title className="fs-5">
              {isEditFromTable
                ? trans("ClientPaymentTab.updateMonthlyPayment")
                : trans("ClientPaymentTab.UpdateRecurringPayment")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row className="mb-1">
              <Col>
                <Form.Group controlId="formCurrencyValue">
                  <FormLabel label={trans("Common.amount")} required />
                  <div className="position-relative">
                    <FormControl
                      type="number"
                      name="currencyValue"
                      value={currencyValue}
                      onChange={onCurrencyValueChange}
                      isInvalid={!!errors.currencyValue}
                      feedback={errors.currencyValue}
                      height={"40"}
                      className="rounded-3 pe-5"
                    />
                    <p
                      className="position-absolute mb-0 lb-text"
                      style={{
                        top: "50%",
                        right: "10px",
                        transform: "translateY(-50%)",
                      }}
                    >
                      {paymentOverview?.currency}
                    </p>
                  </div>
                </Form.Group>
              </Col>

              <Col>
                <Form.Group controlId="formDueDate">
                  <FormLabel
                    label={
                      isEditFromTable
                        ? trans("ClientPaymentTab.dueDate")
                        : trans("ClientPaymentTab.fromDate")
                    }
                    required
                  />
                  <div className="d-flex flex-column">
                    <div
                      className="position-relative date-picker"
                      style={{ width: "fit-content" }}
                    >
                      <DatePicker
                        selected={isEditFromTable ? dueDate : updateFromDate}
                        onChange={(date) =>
                          isEditFromTable
                            ? setDueDate(date)
                            : setUpdateFromDate(date)
                        }
                        dateFormat="MMM d, yyyy"
                        placeholderText="Select Date"
                        className={`custom-datepicker w-100 ${errors.dueDate ? "is-invalid" : ""
                          }`}
                        minDate={
                          isEditFromTable
                            ? tableEditDueDateBounds.minDate
                            : getStripeUtcFirstOfNextMonthMinDate()
                        }
                        maxDate={
                          isEditFromTable
                            ? tableEditDueDateBounds.maxDate
                            : subscriptionEndDate
                              ? parseStripeCalendarDateFromApi(subscriptionEndDate)
                              : null
                        }
                      />
                      <i className="bi bi-calendar3 calendar-icon"></i>
                    </div>
                    {isEditFromTable ? (
                      errors.dueDate && (
                        <div className="invalid-feedback d-block">
                          {errors.dueDate}
                        </div>
                      )
                    ) : (
                      <div className="invalid-feedback d-block">
                        {errors.updateFromDate}
                      </div>
                    )}
                  </div>
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="outline-primary"
              onClick={closeDialog}
              size="sm"
              className="cancel-button w-25 p-1"
            >
              Cancel
            </Button>

            <Button
              size="sm"
              className="saveButton w-25 ml-2 btn btn-primary btn-sm d-flex justify-content-center align-items-center"
              onClick={handleConfirm}
            >
              Update
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* Modal for Pause Subscription and End Subscription */}
      <Modal
        show={isDialogOpen && (isPauseSubscription || isEndSubscription || isExtendSubscription)}
        onHide={closeDialog}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            {isPauseSubscription
              ? trans("ClientPaymentTab.pauseSubscription")
              : isEndSubscription
                ? trans("ClientPaymentTab.endSubscription")
                : trans("ClientPaymentTab.extendSubscription")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {/* Conditional rendering for Pause Subscription */}
          {isPauseSubscription ? (
            <Form.Group controlId="formResumeDate">
              <div className="d-flex flex-column">
                <FormLabel
                  label={trans("ClientPaymentTab.resumeDate")}
                  required
                />
                <div className="d-flex flex-column">
                  <div
                    className="position-relative date-picker"
                    style={{ width: "fit-content" }}
                  >
                    <DatePicker
                      selected={resumeSubscriptionDate}
                      onChange={(date) => setResumeSubscriptionDate(date)}
                      dateFormat="MMM d, yyyy"
                      placeholderText="Select Date"
                      className={`custom-datepicker w-100 ${errors.resumeSubscriptionDate ? "is-invalid" : ""
                        }`}
                      minDate={getStripeUtcDayAfterTomorrowMinDate()}
                    />
                    <i className="bi bi-calendar3 calendar-icon"></i>
                  </div>
                  {errors.resumeSubscriptionDate && (
                    <div className="invalid-feedback d-block">
                      {errors.resumeSubscriptionDate}
                    </div>
                  )}
                </div>
              </div>
            </Form.Group>
          ) : isExtendSubscription ? (
            <Form.Group controlId="formExtendMonths">
              <FormLabel
                label={trans("ClientPaymentTab.extendMonths")}
                required
              />
              <Form.Select
                value={extendMonths}
                onChange={(e) => setExtendMonths(e.target.value)}
              >
                <option value="">Select Months</option>
                {[...Array(12)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1} Month{i + 1 > 1 ? "s" : ""}
                  </option>
                ))}
              </Form.Select>
              {errors.month && (
                <div className="invalid-feedback d-block">
                  {errors.month}
                </div>
              )}
            </Form.Group>
          ) : (
            <Form.Group>
              <Form.Check
                type="radio"
                label="End Subscription Immediately"
                name="endSubscriptionToday"
                checked={selectedOption === "endSubscriptionToday"}
                onChange={() => setSelectedOption("endSubscriptionToday")}
              />
              <Form.Check
                type="radio"
                label="End After Current Payment Period"
                name="endSubscriptionAfterCurrentPaymentPeriod"
                checked={
                  selectedOption === "endSubscriptionAfterCurrentPaymentPeriod"
                }
                onChange={() =>
                  setSelectedOption("endSubscriptionAfterCurrentPaymentPeriod")
                }
              />
            </Form.Group>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={closeDialog}
            size="sm"
            className="back_button w-25 p-1"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="saveButton w-25 ml-2 btn btn-primary btn-sm d-flex justify-content-center align-items-center"
            onClick={handleConfirm}
          >
            {isPauseSubscription ? "Pause" : isExtendSubscription ? "Extend" : "End"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/*Confirmation modal for Pause Subscription and End Subscription */}
      <Modal
        show={isSubscriptionConfirmation}
        onHide={closeSubscriptionConfirmation}
        centered
        backdrop="static"
        keyboard={false}
      >
        <Modal.Header closeButton>
          <Modal.Title className="fs-5">
            {trans("Common.confirmAction")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <FormLabel label={subscriptionConfirmationMessage} className="fs-5" />
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-primary"
            onClick={closeSubscriptionConfirmation}
            size="sm"
            className="back_button w-25 p-1"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            className="saveButton w-25 ml-2 btn btn-primary btn-sm d-flex justify-content-center align-items-center"
            onClick={handleSubscriptionConfirm}
          >
            {isChangePaymentMethod
              ? "Send Mail"
              : isRetry
                ? "Retry"
                : isRefund
                  ? "Issue Refund"
                  : "Confirm"}
          </Button>
        </Modal.Footer>
      </Modal>
      {toastConfig && (
        <ToastNotification
          key={toastConfig.key}
          type={toastConfig.type}
          message={toastConfig.message}
          heading={toastConfig.heading}
        />
      )}
    </div>
  );
};

export default ClientPaymentDetailsTab;
